import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Image, 
  FileText, 
  Zap, 
  Shield, 
  Globe, 
  Smartphone,
  ArrowRight,
  CheckCircle,
  Code,
  Key,
  Target,
  Shuffle,
  Link as LinkIcon,
  Calculator,
  Ruler,
  DollarSign,
  Percent,
  Calendar,
  Weight,
  Clock,
  Timer,
  Dice1,
  Hash,
  TrendingUp,
  MapPin,
  Lock,
  Eye,
  QrCode,
  Scan,
  Radio,
  RotateCcw,
  Search,
  Network,
  Server,
  Activity,
  BarChart3,
  Zap as ZapIcon,
  ExternalLink,
  Monitor,
  Instagram,
  Youtube,
  Music,
  Twitter,
  Facebook,
  Smile,
  Type,
  Share2,
  Square,
  User
} from 'lucide-react'
import { useSEO } from '../hooks/useSEO'

const Home: React.FC = () => {
  // SEO for homepage
  useSEO({
    title: 'SnapTools - Free Online PDF, Image, SEO & Social Media Tools',
    description: 'Convert documents, resize images, optimize SEO, enhance social media. Free PDF converter, image tools, JSON formatter, hashtag generator, meta tags.',
    keywords: 'free PDF converter, online PDF tools, image converter, document converter, JSON formatter, UUID generator, Base64 encoder, JWT decoder, regex tester, developer tools, SEO tools, social media tools, snap-tools.online',
    canonical: 'https://snap-tools.online/',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "SnapTools",
      "url": "https://snap-tools.online",
      "description": "Professional online document, image, and developer tools. Convert PDF, PowerPoint, Excel, Word documents. Format JSON, generate UUIDs, encode Base64, test regex.",
      "applicationCategory": "ProductivityApplication",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "featureList": [
        "PDF Converter",
        "Image Converter", 
        "Document Merger",
        "File Compressor",
        "PowerPoint to PDF",
        "Excel to PDF",
        "Word to PDF",
        "Image Resizer",
        "PDF Splitter",
        "JSON Formatter",
        "UUID Generator",
        "Base64 Encoder",
        "JWT Decoder",
        "Regex Tester",
        "URL Encoder",
        "Text Processing Tools"
      ],
      "publisher": {
        "@type": "Organization",
        "name": "SnapTools",
        "url": "https://snap-tools.online"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "1250"
      }
    }
  })

  const imageTools = [
    {
      title: 'Convert Format',
      description: 'Convert between JPG, PNG, WebP, and GIF formats',
      href: '/image/convert',
      icon: Image,
      features: ['Batch conversion', 'Quality control', 'Metadata preservation']
    },
    {
      title: 'Resize Images',
      description: 'Resize by pixels or percentage with smart aspect ratio handling',
      href: '/image/resize',
      icon: Image,
      features: ['Pixel-perfect sizing', 'Percentage scaling', 'Aspect ratio lock']
    },
    {
      title: 'Compress Images',
      description: 'Reduce file size while maintaining visual quality',
      href: '/image/compress',
      icon: Image,
      features: ['Smart compression', 'Quality preservation', 'Batch processing']
    }
  ]

  const pdfTools = [
    {
      title: 'Merge PDFs',
      description: 'Combine multiple PDF files into a single document',
      href: '/pdf/merge',
      icon: FileText,
      features: ['Drag & drop ordering', 'Page range selection', 'Bookmark preservation']
    },
    {
      title: 'Split PDFs',
      description: 'Extract pages or split PDF into multiple files',
      href: '/pdf/split',
      icon: FileText,
      features: ['Page extraction', 'Range splitting', 'Custom naming']
    },
    {
      title: 'Compress PDFs',
      description: 'Reduce PDF file size without quality loss',
      href: '/pdf/compress',
      icon: FileText,
      features: ['Smart compression', 'Quality control', 'Size optimization']
    }
  ]

  const converterTools = [
    {
      title: 'JPG to PDF',
      description: 'Convert images to PDF documents',
      href: '/convert/jpg-to-pdf',
      icon: Image,
      features: ['Multiple formats', 'Layout options', 'Quality control']
    },
    {
      title: 'Word to PDF',
      description: 'Convert Word documents to PDF format',
      href: '/convert/word-to-pdf',
      icon: FileText,
      features: ['Preserve formatting', 'Font embedding', 'Layout preservation']
    },
    {
      title: 'PowerPoint to PDF',
      description: 'Convert presentations to PDF documents',
      href: '/convert/powerpoint-to-pdf',
      icon: FileText,
      features: ['Slide preservation', 'Note inclusion', 'Animation handling']
    },
    {
      title: 'Excel to PDF',
      description: 'Convert spreadsheets to PDF format',
      href: '/convert/excel-to-pdf',
      icon: FileText,
      features: ['Table formatting', 'Multiple sheets', 'Chart preservation']
    },
    {
      title: 'HTML to PDF',
      description: 'Convert web pages to PDF documents',
      href: '/convert/html-to-pdf',
      icon: FileText,
      features: ['CSS rendering', 'Link preservation', 'Responsive layout']
    },
    {
      title: 'PDF to JPG',
      description: 'Convert PDF pages to image format',
      href: '/convert/pdf-to-jpg',
      icon: Image,
      features: ['High resolution', 'Page selection', 'Format options']
    },
    {
      title: 'PDF to Word',
      description: 'Convert PDF documents to editable Word format',
      href: '/convert/pdf-to-word',
      icon: FileText,
      features: ['Text extraction', 'Format preservation', 'Table recognition']
    },
    {
      title: 'PDF to PowerPoint',
      description: 'Convert PDF to PowerPoint presentations',
      href: '/convert/pdf-to-powerpoint',
      icon: FileText,
      features: ['Slide creation', 'Image extraction', 'Layout preservation']
    },
    {
      title: 'PDF to Excel',
      description: 'Convert PDF tables to Excel spreadsheets',
      href: '/convert/pdf-to-excel',
      icon: FileText,
      features: ['Table detection', 'Data extraction', 'Formula preservation']
    }
  ]

  const textTools = [
    {
      title: 'Word Counter',
      description: 'Count words, characters, sentences, and paragraphs',
      href: '/text/word-counter',
      icon: FileText,
      features: ['Real-time counting', 'Reading time', 'Character analysis']
    },
    {
      title: 'Character Counter',
      description: 'Detailed character analysis and counting',
      href: '/text/character-counter',
      icon: FileText,
      features: ['Alphabetic count', 'Numeric count', 'Symbol analysis']
    },
    {
      title: 'Case Converter',
      description: 'Convert text to different case formats',
      href: '/text/case-converter',
      icon: FileText,
      features: ['Multiple formats', 'camelCase', 'snake_case']
    },
    {
      title: 'Text Sorter',
      description: 'Sort lines of text in various orders',
      href: '/text/text-sorter',
      icon: FileText,
      features: ['Alphabetical sort', 'Length sort', 'Random shuffle']
    },
    {
      title: 'Remove Extra Spaces',
      description: 'Clean up text by removing unwanted spaces',
      href: '/text/remove-extra-spaces',
      icon: FileText,
      features: ['Multiple spaces', 'Leading/trailing', 'Empty lines']
    },
    {
      title: 'Find & Replace',
      description: 'Find and replace text with regex support',
      href: '/text/find-and-replace',
      icon: FileText,
      features: ['Regex support', 'Case sensitive', 'Whole words']
    },
    {
      title: 'Reverse Text',
      description: 'Reverse text in various ways',
      href: '/text/reverse-text',
      icon: FileText,
      features: ['Entire text', 'Word order', 'Each word']
    },
    {
      title: 'Remove Duplicates',
      description: 'Remove duplicate lines from text',
      href: '/text/remove-duplicate-lines',
      icon: FileText,
      features: ['Case options', 'Trim whitespace', 'Keep first/last']
    }
  ]

  const developerTools = [
    {
      title: 'JSON Formatter',
      description: 'Format, validate, and beautify JSON data',
      href: '/dev/json-formatter',
      icon: Code,
      features: ['Syntax validation', 'Pretty print', 'Minify/beautify']
    },
    {
      title: 'JSON ↔ CSV Converter',
      description: 'Convert between JSON and CSV formats',
      href: '/dev/json-csv-converter',
      icon: FileText,
      features: ['Bidirectional', 'Custom delimiters', 'Data type detection']
    },
    {
      title: 'UUID Generator',
      description: 'Generate unique identifiers (v1 and v4)',
      href: '/dev/uuid-generator',
      icon: Shuffle,
      features: ['Bulk generation', 'Multiple formats', 'Copy options']
    },
    {
      title: 'URL Encoder/Decoder',
      description: 'Encode and decode URLs and text',
      href: '/dev/url-encoder',
      icon: LinkIcon,
      features: ['Real-time conversion', 'Bidirectional', 'Sample data']
    },
    {
      title: 'Base64 Encoder',
      description: 'Encode text and files to Base64 format',
      href: '/dev/base64-encoder',
      icon: Key,
      features: ['File support', 'Real-time encoding', 'Size analysis']
    },
    {
      title: 'JWT Decoder',
      description: 'Decode and analyze JSON Web Tokens',
      href: '/dev/jwt-decoder',
      icon: Shield,
      features: ['Header analysis', 'Payload extraction', 'Expiry validation']
    },
    {
      title: 'Regex Tester',
      description: 'Test regular expressions with live matching',
      href: '/dev/regex-tester',
      icon: Target,
      features: ['Live testing', 'Match highlighting', 'Common patterns']
    },
    {
      title: 'XML ↔ JSON Converter',
      description: 'Convert between XML and JSON formats',
      href: '/dev/xml-json-converter',
      icon: Code,
      features: ['Bidirectional', 'Attribute handling', 'Pretty formatting']
    },
    {
      title: 'YAML ↔ JSON Converter',
      description: 'Convert between YAML and JSON formats',
      href: '/dev/yaml-json-converter',
      icon: FileText,
      features: ['Bidirectional', 'Custom indentation', 'Format validation']
    }
  ]

  const mathTools = [
    {
      title: 'Unit Converter',
      description: 'Convert between different units of measurement',
      href: '/calc/unit-converter',
      icon: Ruler,
      features: ['Length, weight, temperature', 'Speed & volume', 'Real-time conversion']
    },
    {
      title: 'Currency Converter',
      description: 'Convert currencies with live exchange rates',
      href: '/calc/currency-converter',
      icon: DollarSign,
      features: ['Live rates', '170+ currencies', 'Historical data']
    },
    {
      title: 'Percentage Calculator',
      description: 'Calculate percentages, increases, and decreases',
      href: '/calc/percentage-calculator',
      icon: Percent,
      features: ['Percentage of value', 'Increase/decrease', 'Percentage change']
    },
    {
      title: 'Age Calculator',
      description: 'Calculate exact age and time differences',
      href: '/calc/age-calculator',
      icon: Calendar,
      features: ['Exact age', 'Days lived', 'Next birthday']
    },
    {
      title: 'BMI Calculator',
      description: 'Calculate Body Mass Index and health metrics',
      href: '/calc/bmi-calculator',
      icon: Weight,
      features: ['BMI calculation', 'Health categories', 'Metric & Imperial']
    },
    {
      title: 'Loan Calculator',
      description: 'Calculate EMI, interest, and loan details',
      href: '/calc/loan-calculator',
      icon: TrendingUp,
      features: ['EMI calculation', 'Payment schedule', 'Interest breakdown']
    },
    {
      title: 'Discount Calculator',
      description: 'Calculate discounts and savings',
      href: '/calc/discount-calculator',
      icon: Percent,
      features: ['Discount amount', 'Final price', 'Savings calculation']
    },
    {
      title: 'Date Calculator',
      description: 'Calculate differences between dates',
      href: '/calc/date-calculator',
      icon: Calendar,
      features: ['Date difference', 'Add/subtract days', 'Business days']
    },
    {
      title: 'Time Zone Converter',
      description: 'Convert time between different time zones',
      href: '/calc/timezone-converter',
      icon: MapPin,
      features: ['Global time zones', 'DST handling', 'Meeting planner']
    },
    {
      title: 'Stopwatch',
      description: 'Precise timing with lap functionality',
      href: '/calc/stopwatch',
      icon: Clock,
      features: ['Millisecond precision', 'Lap times', 'Split timing']
    },
    {
      title: 'Countdown Timer',
      description: 'Set countdown timers for any duration',
      href: '/calc/countdown-timer',
      icon: Timer,
      features: ['Custom duration', 'Visual alerts', 'Multiple timers']
    },
    {
      title: 'Random Number Generator',
      description: 'Generate random numbers with custom ranges',
      href: '/calc/random-number-generator',
      icon: Hash,
      features: ['Custom range', 'Multiple numbers', 'Unique values']
    },
    {
      title: 'Dice Roller',
      description: 'Roll virtual dice for games and decisions',
      href: '/calc/dice-roller',
      icon: Dice1,
      features: ['Multiple dice', 'Custom sides', 'Roll history']
    },
    {
      title: 'Prime Number Checker',
      description: 'Check if numbers are prime and find factors',
      href: '/calc/prime-checker',
      icon: Hash,
      features: ['Prime checking', 'Factor listing', 'Large numbers']
    },
    {
      title: 'Factorial Calculator',
      description: 'Calculate factorial of any number',
      href: '/calc/factorial-calculator',
      icon: Hash,
      features: ['Large factorials', 'Step-by-step', 'Scientific notation']
    },
    {
      title: 'Scientific Calculator',
      description: 'Advanced calculator with scientific functions',
      href: '/calc/scientific-calculator',
      icon: Calculator,
      features: ['Scientific functions', 'History', 'Expression parsing']
    }
  ]

  const securityTools = [
    {
      title: 'MD5 / SHA256 Hash Generator',
      description: 'Generate MD5, SHA1, SHA256 hashes for text and files',
      href: '/security/hash-generator',
      icon: Hash,
      features: ['Multiple algorithms', 'File hashing', 'Text hashing']
    },
    {
      title: 'Password Generator',
      description: 'Generate secure passwords with custom strength',
      href: '/security/password-generator',
      icon: Lock,
      features: ['Custom length', 'Character sets', 'Strength meter']
    },
    {
      title: 'Password Strength Checker',
      description: 'Analyze password security and get improvement tips',
      href: '/security/password-checker',
      icon: Eye,
      features: ['Security score', 'Improvement tips', 'Breach check']
    },
    {
      title: 'QR Code Generator',
      description: 'Create QR codes for text, URLs, and data',
      href: '/security/qr-generator',
      icon: QrCode,
      features: ['Custom sizes', 'Multiple formats', 'Logo embedding']
    },
    {
      title: 'Barcode Generator',
      description: 'Generate various barcode formats',
      href: '/security/barcode-generator',
      icon: Scan,
      features: ['Multiple formats', 'Custom sizes', 'High quality']
    },
    {
      title: 'Morse Code Encoder/Decoder',
      description: 'Convert text to Morse code and vice versa',
      href: '/security/morse-code',
      icon: Radio,
      features: ['Audio playback', 'Custom speed', 'International morse']
    },
    {
      title: 'ROT13 Converter',
      description: 'Simple letter substitution cipher',
      href: '/security/rot13',
      icon: RotateCcw,
      features: ['Instant conversion', 'Preserves formatting', 'Bidirectional']
    },
    {
      title: 'Caesar Cipher Tool',
      description: 'Encrypt/decrypt text with Caesar cipher',
      href: '/security/caesar-cipher',
      icon: Lock,
      features: ['Custom shift', 'Brute force', 'Frequency analysis']
    },
    {
      title: 'File Hash Checker',
      description: 'Verify file integrity with hash checking',
      href: '/security/file-hash',
      icon: Shield,
      features: ['MD5, SHA1, SHA256', 'File verification', 'Integrity check']
    },
    {
      title: 'IP Lookup Tool',
      description: 'Get detailed information about IP addresses',
      href: '/security/ip-lookup',
      icon: Search,
      features: ['Geolocation', 'ISP info', 'Security details']
    },
    {
      title: 'WHOIS Lookup',
      description: 'Domain registration and ownership information',
      href: '/security/whois-lookup',
      icon: Network,
      features: ['Domain details', 'Registration info', 'Nameservers']
    },
    {
      title: 'DNS Lookup',
      description: 'Query DNS records for any domain',
      href: '/security/dns-lookup',
      icon: Server,
      features: ['All record types', 'Multiple servers', 'Detailed results']
    },
    {
      title: 'Ping / Traceroute',
      description: 'Test network connectivity and routing',
      href: '/security/ping-traceroute',
      icon: Activity,
      features: ['Network testing', 'Route tracing', 'Response times']
    }
  ]

  const seoTools = [
    {
      title: 'SEO Analyzer',
      description: 'Comprehensive SEO analysis and optimization recommendations',
      href: '/seo/meta-tag-generator',
      icon: BarChart3,
      features: ['Meta tag analysis', 'Content optimization', 'Performance scores']
    },
    {
      title: 'Meta Tag Generator',
      description: 'Generate optimized meta tags for better search rankings',
      href: '/seo/meta-tag-generator',
      icon: Code,
      features: ['Title & description', 'Open Graph tags', 'Twitter cards']
    },
    {
      title: 'Keyword Density Checker',
      description: 'Analyze keyword density and content optimization',
      href: '/seo/word-density-analyzer',
      icon: Target,
      features: ['Keyword analysis', 'Density calculation', 'Optimization tips']
    },
    {
      title: 'Robots.txt Generator',
      description: 'Create and validate robots.txt files for search engines',
      href: '/seo/robots-txt-generator',
      icon: Search,
      features: ['Custom rules', 'Validation', 'Best practices']
    },
    {
      title: 'Sitemap Generator',
      description: 'Generate XML sitemaps for better search engine indexing',
      href: '/seo/sitemap-generator',
      icon: Search,
      features: ['XML sitemap', 'Priority settings', 'Frequency control']
    },
    {
      title: 'Page Speed Analyzer',
      description: 'Analyze website loading speed and performance metrics',
      href: '/seo/page-speed-tester',
      icon: Activity,
      features: ['Speed metrics', 'Performance tips', 'Core Web Vitals']
    },
    {
      title: 'Schema Markup Generator',
      description: 'Generate structured data markup for rich snippets',
      href: '/seo/meta-tag-generator',
      icon: Code,
      features: ['Multiple schemas', 'JSON-LD format', 'Validation']
    },
    {
      title: 'Open Graph Generator',
      description: 'Create Open Graph meta tags for social media sharing',
      href: '/seo/open-graph-preview',
      icon: Share2,
      features: ['Social previews', 'Image optimization', 'Platform specific']
    },
    {
      title: 'Broken Link Checker',
      description: 'Find and analyze broken links on any webpage',
      href: '/seo/broken-link-checker',
      icon: ExternalLink,
      features: ['Link validation', 'Status codes', 'Bulk checking']
    },
    {
      title: 'SERP Preview Tool',
      description: 'Preview how your page appears in search engine results',
      href: '/seo/serp-snippet-preview',
      icon: Monitor,
      features: ['Google preview', 'Title & description', 'Rich snippets']
    },
    {
      title: 'Website Speed Test',
      description: 'Comprehensive website performance and speed analysis',
      href: '/seo/page-speed-tester',
      icon: ZapIcon,
      features: ['Loading times', 'Performance metrics', 'Optimization tips']
    }
  ]

  const designTools = [
    {
      title: 'CSS Box Shadow Generator',
      description: 'Create beautiful box shadows with live preview and CSS code generation',
      href: '/design/css-box-shadow-generator',
      icon: Square,
      features: ['Multiple shadows', 'Live preview', 'CSS export']
    },
    {
      title: 'CSS Border Radius Generator',
      description: 'Generate rounded corners with individual corner control and presets',
      href: '/design/css-border-radius-generator',
      icon: Square,
      features: ['Individual corners', 'Presets', 'Live preview']
    },
    {
      title: 'QR vCard Generator',
      description: 'Create digital business card QR codes with contact information',
      href: '/design/qr-vcard-generator',
      icon: QrCode,
      features: ['Digital business cards', 'Contact QR codes', 'vCard export']
    },
    {
      title: 'Resume Builder',
      description: 'Build professional resumes with templates and PDF export',
      href: '/design/resume-builder',
      icon: FileText,
      features: ['Professional templates', 'PDF export', 'ATS friendly']
    },
    {
      title: 'Business Card Generator',
      description: 'Design and create professional business cards online',
      href: '/design/business-card-generator',
      icon: User,
      features: ['Multiple designs', 'Print ready', 'Custom branding']
    },
    {
      title: 'Calendar Generator',
      description: 'Generate custom calendars for any month and year',
      href: '/design/calendar-generator',
      icon: Calendar,
      features: ['Custom months', 'Print ready', 'Event marking']
    },
    {
      title: 'ASCII Art Generator',
      description: 'Convert text and images to ASCII art with various styles',
      href: '/design/ascii-art-generator',
      icon: Type,
      features: ['Text to ASCII', 'Image to ASCII', 'Multiple styles']
    },
    {
      title: 'Flowchart Maker',
      description: 'Create flowcharts and diagrams with SVG export functionality',
      href: '/design/flowchart-maker',
      icon: Network,
      features: ['Drag & drop', 'SVG export', 'Professional diagrams']
    },
    {
      title: 'Time Tracker',
      description: 'Pomodoro timer and productivity tracker for better focus',
      href: '/design/time-tracker',
      icon: Timer,
      features: ['Pomodoro technique', 'Task tracking', 'Productivity stats']
    }
  ]

  const socialMediaTools = [
    {
      title: 'Instagram Profile Downloader',
      description: 'Download Instagram profile photos and view user statistics',
      href: '/social/instagram-profile-downloader',
      icon: Instagram,
      features: ['Profile photos', 'User statistics', 'HD quality']
    },
    {
      title: 'YouTube Thumbnail Downloader',
      description: 'Extract and download YouTube video thumbnails in various qualities',
      href: '/social/youtube-thumbnail-downloader',
      icon: Youtube,
      features: ['Multiple qualities', 'Batch download', 'Video metadata']
    },
    {
      title: 'TikTok Video Downloader',
      description: 'Download TikTok videos without watermarks in HD quality',
      href: '/social/tiktok-video-downloader',
      icon: Music,
      features: ['No watermark', 'HD quality', 'MP4/MP3 formats']
    },
    {
      title: 'Twitter Thread Unroller',
      description: 'Convert Twitter threads into readable text formats',
      href: '/social/twitter-thread-unroller',
      icon: Twitter,
      features: ['Thread extraction', 'Multiple formats', 'Export options']
    },
    {
      title: 'Facebook Video Downloader',
      description: 'Download Facebook videos in HD and SD quality',
      href: '/social/facebook-video-downloader',
      icon: Facebook,
      features: ['HD/SD quality', 'Video metadata', 'Fast download']
    },
    {
      title: 'Hashtag Generator',
      description: 'Generate trending hashtags for social media platforms',
      href: '/social/hashtag-generator',
      icon: Hash,
      features: ['Platform specific', 'Trending tags', 'Bulk generation']
    },
    {
      title: 'Social Share Preview',
      description: 'Preview how your content appears when shared on social media',
      href: '/social/social-share-preview',
      icon: Share2,
      features: ['Multi-platform', 'Real previews', 'Optimization tips']
    },
    {
      title: 'Emoji Picker',
      description: 'Browse, search, and copy emojis for social media posts',
      href: '/social/emoji-picker',
      icon: Smile,
      features: ['All categories', 'Search function', 'Recent history']
    },
    {
      title: 'Stylish Text Generator',
      description: 'Create stylish text with various fonts and Unicode styles',
      href: '/social/stylish-text-generator',
      icon: Type,
      features: ['Multiple styles', 'Unicode fonts', 'Copy & paste']
    }
  ]

  const features = [
    {
      icon: Shield,
      title: 'Privacy First',
      description: 'All processing happens in your browser. Your files never leave your device.'
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Client-side processing means instant results without server delays.'
    },
    {
      icon: Globe,
      title: 'Works Everywhere',
      description: 'Access your tools from any device with a modern web browser.'
    },
    {
      icon: Smartphone,
      title: 'Mobile Optimized',
      description: 'Full functionality on desktop, tablet, and mobile devices.'
    }
  ]

  const ToolCard = ({ tool, index }: { tool: any, index: number }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 group"
    >
      <div className="flex items-center mb-4">
        <div className="w-12 h-12 bg-gradient-to-r from-[#8438ff] to-[#5d2ca8] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
          <tool.icon className="h-6 w-6 text-white" />
        </div>
        <div className="ml-4">
          <h3 className="text-xl font-semibold text-gray-900 group-hover:text-[#8438ff] transition-colors duration-300">
            {tool.title}
          </h3>
        </div>
      </div>
      
      <p className="text-gray-600 mb-4 line-clamp-2">
        {tool.description}
      </p>
      
      <div className="mb-4">
        {tool.features.map((feature: string, idx: number) => (
          <div key={idx} className="flex items-center mb-2">
            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
            <span className="text-sm text-gray-600">{feature}</span>
          </div>
        ))}
      </div>
      
      <Link 
        to={tool.href}
        className="inline-flex items-center text-[#8438ff] font-medium hover:text-[#7228ee] transition-colors duration-300 group"
      >
        Try it now
        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
      </Link>
    </motion.div>
  )

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-6 bg-gradient-to-br from-[#8438ff] to-[#5d2ca8]">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Free Online PDF, Image, SEO & Social Media Tools
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-4xl mx-auto">
              Convert documents, process images, optimize SEO, and enhance social media content with professional-grade tools. 
              JSON formatting, UUID generation, Base64 encoding, hashtag generation, meta tag optimization, and more. All browser-based, secure, and free.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/image/convert" className="btn-primary bg-white text-[#8438ff] hover:bg-gray-100">
                Start Converting Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/pdf/merge" className="btn-primary bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#8438ff] transition-colors">
                Explore PDF Tools
                <FileText className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section with Internal Links */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Complete Online Tool Suite for Professionals
            </h2>
            <div className="max-w-4xl mx-auto text-lg text-gray-700 space-y-4">
              <p>
                SnapTools provides a comprehensive collection of <Link to="/pdf/convert" className="text-[#8438ff] hover:underline font-semibold">PDF conversion tools</Link>, 
                <Link to="/image/resize" className="text-[#8438ff] hover:underline font-semibold ml-1">image processing utilities</Link>, 
                <Link to="/seo/meta-tag-generator" className="text-[#8438ff] hover:underline font-semibold ml-1">SEO optimization tools</Link>, and 
                <Link to="/social/youtube-thumbnail" className="text-[#8438ff] hover:underline font-semibold ml-1">social media enhancement features</Link>.
              </p>
              <p>
                Whether you need to <Link to="/convert/word-to-pdf" className="text-[#8438ff] hover:underline font-semibold">convert Word documents to PDF</Link>, 
                <Link to="/image/compress" className="text-[#8438ff] hover:underline font-semibold ml-1">compress images</Link>, 
                <Link to="/text/json-formatter" className="text-[#8438ff] hover:underline font-semibold ml-1">format JSON</Link>, or 
                <Link to="/social/hashtag-generator" className="text-[#8438ff] hover:underline font-semibold ml-1">generate hashtags</Link>, 
                our tools work entirely in your browser with no uploads required.
              </p>
              <p>
                Popular tools include our <Link to="/pdf/merge" className="text-[#8438ff] hover:underline font-semibold">PDF merger</Link>, 
                <Link to="/developer/uuid-generator" className="text-[#8438ff] hover:underline font-semibold ml-1">UUID generator</Link>, 
                <Link to="/developer/base64-encoder" className="text-[#8438ff] hover:underline font-semibold ml-1">Base64 encoder</Link>, and 
                <Link to="/design/css-box-shadow-generator" className="text-[#8438ff] hover:underline font-semibold ml-1">CSS generators</Link>.
              </p>
            </div>
          </div>

          {/* Quick Access Categories */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Document Tools</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/pdf/convert" className="text-[#8438ff] hover:underline">PDF Converter</Link></li>
                <li><Link to="/pdf/merge" className="text-[#8438ff] hover:underline">PDF Merger</Link></li>
                <li><Link to="/convert/word-to-pdf" className="text-[#8438ff] hover:underline">Word to PDF</Link></li>
                <li><Link to="/convert/excel-to-pdf" className="text-[#8438ff] hover:underline">Excel to PDF</Link></li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Image Tools</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/image/resize" className="text-[#8438ff] hover:underline">Image Resizer</Link></li>
                <li><Link to="/image/compress" className="text-[#8438ff] hover:underline">Image Compressor</Link></li>
                <li><Link to="/image/convert" className="text-[#8438ff] hover:underline">Format Converter</Link></li>
                <li><Link to="/convert/jpg-to-pdf" className="text-[#8438ff] hover:underline">JPG to PDF</Link></li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">SEO Tools</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/seo/meta-tag-generator" className="text-[#8438ff] hover:underline">Meta Tag Generator</Link></li>
                <li><Link to="/seo/sitemap-generator" className="text-[#8438ff] hover:underline">Sitemap Generator</Link></li>
                <li><Link to="/seo/robots-txt-generator" className="text-[#8438ff] hover:underline">Robots.txt Generator</Link></li>
                <li><Link to="/seo/broken-link-checker" className="text-[#8438ff] hover:underline">Link Checker</Link></li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Developer Tools</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/text/json-formatter" className="text-[#8438ff] hover:underline">JSON Formatter</Link></li>
                <li><Link to="/developer/uuid-generator" className="text-[#8438ff] hover:underline">UUID Generator</Link></li>
                <li><Link to="/developer/base64-encoder" className="text-[#8438ff] hover:underline">Base64 Encoder</Link></li>
                <li><Link to="/text/regex-tester" className="text-[#8438ff] hover:underline">Regex Tester</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Tools Grid */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Featured Tools by Category
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional-grade tools for document conversion, image processing, text manipulation, SEO optimization, social media enhancement, and developer utilities. 
              Fast, secure, and completely free to use.
            </p>
          </motion.div>

          {/* Image Tools */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Image Processing Tools</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {imageTools.map((tool, index) => (
                <ToolCard key={tool.title} tool={tool} index={index} />
              ))}
            </div>
          </div>

          {/* PDF Tools */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">PDF Processing Tools</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {pdfTools.map((tool, index) => (
                <ToolCard key={tool.title} tool={tool} index={index} />
              ))}
            </div>
          </div>

          {/* Converter Tools */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Document Conversion Tools</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {converterTools.map((tool, index) => (
                <ToolCard key={tool.title} tool={tool} index={index} />
              ))}
            </div>
          </div>

          {/* Text Tools */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Text Processing Tools</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {textTools.map((tool, index) => (
                <ToolCard key={tool.title} tool={tool} index={index} />
              ))}
            </div>
          </div>

          {/* Developer Tools */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Developer & Data Tools</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {developerTools.map((tool, index) => (
                <ToolCard key={tool.title} tool={tool} index={index} />
              ))}
            </div>
          </div>

          {/* Math, Time & Calculator Tools */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Math, Time & Calculator Tools</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {mathTools.map((tool, index) => (
                <ToolCard key={tool.title} tool={tool} index={index} />
              ))}
            </div>
          </div>

          {/* Security & Encoding Tools */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Security & Encoding Tools</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {securityTools.map((tool, index) => (
                <ToolCard key={tool.title} tool={tool} index={index} />
              ))}
            </div>
          </div>

          {/* SEO & Website Tools */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">SEO & Website Tools</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {seoTools.map((tool, index) => (
                <ToolCard key={tool.title} tool={tool} index={index} />
              ))}
            </div>
          </div>

          {/* Design & Misc Tools */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Design & Misc Tools</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {designTools.map((tool, index) => (
                <ToolCard key={tool.title} tool={tool} index={index} />
              ))}
            </div>
          </div>

          {/* Social Media Tools */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Social Media Tools</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {socialMediaTools.map((tool, index) => (
                <ToolCard key={tool.title} tool={tool} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why choose SnapTools?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built with privacy, performance, and usability in mind. 
              Experience the difference of truly modern web tools.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#5d2ca8] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comprehensive Content Section for SEO */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Complete Guide to Online Document and Image Processing
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">PDF Document Tools</h3>
                <p className="text-gray-700 mb-4">
                  Our comprehensive <Link to="/pdf/convert" className="text-[#8438ff] hover:underline">PDF conversion suite</Link> handles all your document needs. 
                  Convert <Link to="/convert/word-to-pdf" className="text-[#8438ff] hover:underline">Word documents to PDF</Link>, 
                  <Link to="/convert/excel-to-pdf" className="text-[#8438ff] hover:underline ml-1">Excel spreadsheets to PDF</Link>, and 
                  <Link to="/convert/powerpoint-to-pdf" className="text-[#8438ff] hover:underline ml-1">PowerPoint presentations to PDF</Link> 
                  while maintaining perfect formatting and layout.
                </p>
                <p className="text-gray-700">
                  Advanced features include <Link to="/pdf/merge" className="text-[#8438ff] hover:underline">PDF merging</Link>, 
                  <Link to="/pdf/split" className="text-[#8438ff] hover:underline ml-1">PDF splitting</Link>, and 
                  <Link to="/pdf/compress" className="text-[#8438ff] hover:underline ml-1">PDF compression</Link> 
                  to optimize file sizes without quality loss.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Image Processing Solutions</h3>
                <p className="text-gray-700 mb-4">
                  Professional <Link to="/image/resize" className="text-[#8438ff] hover:underline">image resizing tools</Link> support batch processing 
                  with smart aspect ratio handling. <Link to="/image/compress" className="text-[#8438ff] hover:underline">Compress images</Link> to reduce 
                  file sizes while maintaining visual quality, or <Link to="/image/convert" className="text-[#8438ff] hover:underline">convert between formats</Link> 
                  including JPG, PNG, WebP, and GIF.
                </p>
                <p className="text-gray-700">
                  Convert images to PDF with our <Link to="/convert/jpg-to-pdf" className="text-[#8438ff] hover:underline">JPG to PDF converter</Link> 
                  or extract images from PDFs using our <Link to="/convert/pdf-to-jpg" className="text-[#8438ff] hover:underline">PDF to JPG tool</Link>.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">SEO and Web Optimization</h3>
                <p className="text-gray-700 mb-4">
                  Boost your website's search engine visibility with our <Link to="/seo/meta-tag-generator" className="text-[#8438ff] hover:underline">meta tag generator</Link>, 
                  <Link to="/seo/sitemap-generator" className="text-[#8438ff] hover:underline ml-1">sitemap generator</Link>, and 
                  <Link to="/seo/robots-txt-generator" className="text-[#8438ff] hover:underline ml-1">robots.txt generator</Link>. 
                  Analyze your site with our <Link to="/seo/broken-link-checker" className="text-[#8438ff] hover:underline">broken link checker</Link> 
                  and <Link to="/seo/page-speed-tester" className="text-[#8438ff] hover:underline ml-1">page speed tester</Link>.
                </p>
                <p className="text-gray-700">
                  Generate optimized content with our <Link to="/social/hashtag-generator" className="text-[#8438ff] hover:underline">hashtag generator</Link> 
                  and <Link to="/seo/word-density-analyzer" className="text-[#8438ff] hover:underline ml-1">keyword density analyzer</Link> 
                  to improve your content strategy.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Developer and Text Tools</h3>
                <p className="text-gray-700 mb-4">
                  Essential developer utilities include our <Link to="/text/json-formatter" className="text-[#8438ff] hover:underline">JSON formatter</Link>, 
                  <Link to="/developer/uuid-generator" className="text-[#8438ff] hover:underline ml-1">UUID generator</Link>, 
                  <Link to="/developer/base64-encoder" className="text-[#8438ff] hover:underline ml-1">Base64 encoder/decoder</Link>, and 
                  <Link to="/text/regex-tester" className="text-[#8438ff] hover:underline ml-1">regex testing tool</Link>. 
                  Process text with our <Link to="/text/case-converter" className="text-[#8438ff] hover:underline">case converter</Link> 
                  and <Link to="/text/word-counter" className="text-[#8438ff] hover:underline ml-1">word counter</Link>.
                </p>
                <p className="text-gray-700">
                  Secure password generation with our <Link to="/security/password-generator" className="text-[#8438ff] hover:underline">password generator</Link> 
                  and <Link to="/security/password-strength-checker" className="text-[#8438ff] hover:underline ml-1">password strength checker</Link> 
                  ensures your accounts stay protected.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Why Choose SnapTools for Your Document Processing Needs?</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-[#8438ff] mr-2">•</span>
                  <strong>100% Browser-Based:</strong> All processing happens locally in your browser for maximum security and privacy
                </li>
                <li className="flex items-start">
                  <span className="text-[#8438ff] mr-2">•</span>
                  <strong>No File Uploads:</strong> Your documents never leave your device, ensuring complete data privacy
                </li>
                <li className="flex items-start">
                  <span className="text-[#8438ff] mr-2">•</span>
                  <strong>Professional Quality:</strong> Industry-standard processing algorithms deliver professional results
                </li>
                <li className="flex items-start">
                  <span className="text-[#8438ff] mr-2">•</span>
                  <strong>Free to Use:</strong> Access all tools without registration, subscriptions, or hidden fees
                </li>
                <li className="flex items-start">
                  <span className="text-[#8438ff] mr-2">•</span>
                  <strong>Mobile Friendly:</strong> Responsive design works perfectly on desktop, tablet, and mobile devices
                </li>
                <li className="flex items-start">
                  <span className="text-[#8438ff] mr-2">•</span>
                  <strong>Fast Processing:</strong> Optimized algorithms ensure quick processing even for large files
                </li>
              </ul>
            </div>

            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Start Processing Your Documents Today</h3>
              <p className="text-gray-700 mb-6">
                Join thousands of professionals who trust SnapTools for their document processing, image optimization, 
                SEO enhancement, and development needs. Our comprehensive suite of tools covers everything from basic 
                file conversion to advanced web optimization and developer utilities.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/pdf/convert" className="inline-flex items-center px-6 py-3 bg-[#8438ff] text-white rounded-lg hover:bg-[#7228ee] transition-colors">
                  Try PDF Tools
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link to="/image/resize" className="inline-flex items-center px-6 py-3 bg-[#8438ff] text-white rounded-lg hover:bg-[#7228ee] transition-colors">
                  Try Image Tools
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link to="/seo/meta-tag-generator" className="inline-flex items-center px-6 py-3 bg-[#8438ff] text-white rounded-lg hover:bg-[#7228ee] transition-colors">
                  Try SEO Tools
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-[#8438ff] to-[#5d2ca8]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Transform any document with professional tools
            </h2>
            <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
              Start converting your files today with our comprehensive suite of document conversion tools. 
              No registration required, completely free to use.
            </p>
            <Link to="/image/convert" className="btn-primary bg-white text-[#8438ff] hover:bg-gray-100 hover:text-[#7228ee]">
              Try it for yourself
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home
